import React from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth, primaryRole } from "@/lib/auth";
import { roleLabel } from "@/lib/rolePolicy";
import { useI18n } from "@/i18n";
import { CAPABILITY, navigationForUser } from "@/lib/capabilityPolicy";
import { usePublicBranding } from "@/lib/platformConfig";
import LanguageSelector from "@/components/LanguageSelector";
import {
  LayoutDashboard,
  Users,
  GraduationCap,
  ClipboardCheck,
  Wallet,
  Megaphone,
  Building2,
  UserCog,
  LogOut,
  ChevronDown,
  BookOpen,
  FileSpreadsheet,
  Menu,
  X,
  Home,
  CalendarClock,
  FileEdit,
  ScrollText,
  Library,
  GraduationCap as ExamIcon,
  Bus,
  ShieldAlert,
  FileText,
  TrendingUp,
  Sparkles,
  Calculator,
  Settings2,
  Camera,
  Package,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { TENANT_MANAGEMENT_ROLES } from "@/lib/rolePolicy";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
} from "@/components/ui/sheet";

const MANAGEMENT_NAV = [
    { to: "/principal", labelKey: "nav.dashboard", icon: LayoutDashboard },
    { to: "/admissions", labelKey: "nav.admissions", icon: FileSpreadsheet },
    { to: "/students", labelKey: "nav.students", icon: Users },
    { to: "/attendance", labelKey: "nav.attendance", icon: ClipboardCheck },
    { to: "/timetable", labelKey: "nav.timetable", icon: CalendarClock },
    { to: "/exams", labelKey: "nav.exams", icon: ExamIcon },
    { to: "/library", labelKey: "nav.library", icon: Library },
    { to: "/services", labelKey: "nav.services", icon: Bus },
    { to: "/announcements", labelKey: "nav.announcements", icon: Megaphone },
    { to: "/erp", labelKey: "nav.erp", icon: UserCog },
    { to: "/inventory", labelKey: "nav.inventory", icon: Package },
    { to: "/campus", labelKey: "nav.campus", icon: ShieldAlert },
    { to: "/nursery", labelKey: "nav.nursery", icon: Sparkles },
    { to: "/fees", labelKey: "nav.fees", icon: Wallet },
    { to: "/academic", labelKey: "nav.academicSetup", icon: BookOpen },
    { to: "/staff", labelKey: "nav.staff", icon: UserCog },
    { to: "/dcuments", labelKey: "nav.dcuments", icon: FileText },
    { to: "/institution", labelKey: "nav.institution", icon: Building2 },
    {
      to: "/platform/settings",
      labelKey: "nav.platformSettings",
      icon: Settings2,
    },
    { to: "/reports", labelKey: "nav.reports", icon: FileText },
    { to: "/kiosk", labelKey: "nav.kiosk", icon: ShieldAlert },
];

const NAV_BY_ROLE = {
  management: MANAGEMENT_NAV,
  accountant: [
    { to: "/fees", labelKey: "nav.fees", icon: Wallet },
    { to: "/students", labelKey: "nav.students", icon: Users },
    { to: "/library", labelKey: "nav.library", icon: Library },
  ],
  teacher: [
    { to: "/teacher", labelKey: "nav.home", icon: Home },
    {
      to: "/teacher/attendance",
      labelKey: "nav.attendance",
      icon: ClipboardCheck,
    },
    { to: "/teacher/homework", labelKey: "nav.homework", icon: FileEdit },
    { to: "/teacher/marks", labelKey: "nav.marks", icon: ScrollText },
    { to: "/announcements", labelKey: "nav.announcements", icon: Megaphone },
    { to: "/inventory", labelKey: "nav.inventory", icon: Package },
  ],
  guardian: [
    { to: "/parent", labelKey: "nav.home", icon: Home },
    { to: "/parent/gallery", labelKey: "nav.gallery", icon: Camera },
    { to: "/parent/study", labelKey: "nav.study", icon: BookOpen },
    { to: "/parent/services", labelKey: "nav.services", icon: Bus },
    { to: "/parent/fees", labelKey: "nav.fees", icon: Wallet },
  ],
};

const NAV_CAPABILITY_BY_PATH = {
  "/principal": CAPABILITY.DASHBOARD_READ,
  "/admissions": [CAPABILITY.ADMISSIONS_MANAGE, CAPABILITY.REPORTS_READ],
  "/students": [
    CAPABILITY.STUDENTS_READ,
    CAPABILITY.REPORTS_READ,
    CAPABILITY.ACADEMIC_READ,
    CAPABILITY.FINANCE_READ,
  ],
  "/attendance": CAPABILITY.ATTENDANCE_MANAGE,
  "/timetable": CAPABILITY.ACADEMIC_MANAGE,
  "/exams": CAPABILITY.ACADEMIC_ASSESS,
  "/library": CAPABILITY.RESOURCES_READ,
  "/services": CAPABILITY.TRANSPORT_MANAGE,
  "/announcements": [CAPABILITY.COMMUNICATION_MANAGE, CAPABILITY.REPORTS_READ],
  "/erp": CAPABILITY.RESOURCES_READ,
  "/inventory": CAPABILITY.INVENTORY_ISSUE,
  "/campus": [CAPABILITY.SAFETY_READ, CAPABILITY.SAFETY_MANAGE],
  "/nursery": CAPABILITY.NURSERY_MANAGE,
  "/fees": CAPABILITY.FINANCE_READ,
  "/circulars": [CAPABILITY.COMMUNICATION_MANAGE, CAPABILITY.REPORTS_READ],
  "/academic": CAPABILITY.ACADEMIC_MANAGE,
  "/staff": CAPABILITY.STAFF_MANAGE,
  "/dcuments": [CAPABILITY.STAFF_MANAGE, CAPABILITY.ADMISSIONS_MANAGE, CAPABILITY.DOCUMENTS_READ],
  "/institution": CAPABILITY.TENANT_SETTINGS_READ,
  "/platform/settings": CAPABILITY.PLATFORM_CONFIG_READ,
  "/platform/branding": CAPABILITY.PLATFORM_CONFIG_READ,
  "/platform/configuration": CAPABILITY.PLATFORM_CONFIG_READ,
  "/platform/lookups": CAPABILITY.PLATFORM_CONFIG_READ,
  "/teacher": CAPABILITY.ACADEMIC_READ,
  "/teacher/attendance": CAPABILITY.ATTENDANCE_MANAGE,
  "/teacher/homework": CAPABILITY.ACADEMIC_READ,
  "/teacher/marks": CAPABILITY.ACADEMIC_ASSESS,
  "/parent": [CAPABILITY.STUDENTS_READ, CAPABILITY.REPORTS_READ],
  "/parent/study": [CAPABILITY.ACADEMIC_READ, CAPABILITY.REPORTS_READ],
  "/parent/gallery": [CAPABILITY.STUDENTS_READ, CAPABILITY.REPORTS_READ],
  "/parent/services": [CAPABILITY.TRANSPORT_READ, CAPABILITY.REPORTS_READ],
  "/parent/fees": CAPABILITY.FINANCE_READ,
  "/parent/circulars": CAPABILITY.COMMUNICATION_MANAGE,
  "/account": null,
};

const HIDE_SERVICES_NAV_ROLES = new Set(["administrator", "principal"]);

const navigationItemsForUser = (user, t) => {
  const entries = (user?.roles || []).flatMap(
    (role) => NAV_BY_ROLE[TENANT_MANAGEMENT_ROLES.includes(role) ? "management" : role] || [],
  );
  const hideServicesNav = (user?.roles || []).some((role) => HIDE_SERVICES_NAV_ROLES.has(role));
  const deduped = [
    ...new Map(entries.map((item) => [item.to, item])).values(),
  ].filter((item) => !(hideServicesNav && item.to === "/services"))
   .map((item) => ({
    ...item,
    label: t(item.labelKey),
    capability: NAV_CAPABILITY_BY_PATH[item.to],
  }));
  return navigationForUser(user, deduped);
};

function SidebarLinks({ user, onNavigate, t }) {
  const nav = navigationItemsForUser(user, t);
  return (
    <nav className="flex flex-col gap-1 p-3">
      {nav.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          onClick={onNavigate}
          data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
          className={({ isActive }) =>
            cn(
              "side-link flex items-center gap-3 rounded-md px-4 py-2.5 text-sm font-medium",
              isActive
                ? "bg-white/10 text-white"
                : "text-white/70 hover:bg-white/5 hover:text-white",
            )
          }
        >
          {({ isActive }) => (
            <span
              data-active={isActive}
              className="flex items-center gap-3 w-full"
            >
              <item.icon className="h-4 w-4" strokeWidth={2} />
              {item.label}
            </span>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

function BrandBadge({ appName, clientName }) {
  const { t } = useI18n();
  const initial = (appName || "E").trim().slice(0, 1).toUpperCase() || "E";
  const schoolName = clientName || t("shell.educationOs");
  return (
    <div className="px-5 pt-6 pb-4 border-b border-white/10">
      <div className="flex items-center gap-2 text-white">
        <div className="h-9 w-9 rounded-md bg-[#C05621] flex items-center justify-center font-display font-bold text-lg">
          {initial}
        </div>
        <div>
          <div className="font-display text-lg font-bold leading-tight">
            {appName}
          </div>
          <div className="text-[11px] uppercase tracking-widest text-white/50 leading-tight">
            {clientName || t("shell.educationOs")}
          </div>
        </div>
      </div>
      <div className="mt-4 text-white/80 text-sm truncate">{schoolName}</div>
      <LanguageSelector compact className="mt-4" />
    </div>
  );
}

function UserFooter({ user, onLogout }) {
  const { t } = useI18n();
  return (
    <div className="mt-auto border-t border-white/10 p-4 flex items-center gap-3">
      <div className="h-9 w-9 rounded-full bg-white/10 flex items-center justify-center text-white font-semibold">
        {(user?.user?.full_name || "?").slice(0, 1)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-white truncate">
          {user?.user?.full_name}
        </div>
        <div className="text-[11px] text-white/50 truncate">
          {(user?.roles || []).map(roleLabel).join(", ")}
        </div>
      </div>
      <Button
        variant="ghost"
        size="icon"
        onClick={onLogout}
        data-testid="logout-btn"
        className="text-white/70 hover:text-white hover:bg-white/10"
      >
        <LogOut className="h-4 w-4" aria-label={t("shell.logout")} />
        <span className="sr-only">{t("shell.logout")}</span>
      </Button>
    </div>
  );
}

export default function ShellLayout({ children }) {
  const { user, logout } = useAuth();
  const { direction, t } = useI18n();
  const branding = usePublicBranding();
  const location = useLocation();
  const role = primaryRole(user?.roles || []);
  const isMobileRole = role === "guardian" || role === "teacher";
  const appName = branding.data?.app_name || "EduOS";
  const clientName = branding.data?.client_name || t("shell.educationOs");

  // Mobile bottom nav for guardian/teacher
  if (isMobileRole) {
    const nav = navigationItemsForUser(user, t);
    return (
      <div className="min-h-screen bg-[var(--bg)] pb-20">
        <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-xl border-b border-[var(--subtle)]">
          <div className="max-w-2xl mx-auto flex items-center justify-between px-4 py-3">
            <div>
              <div
                className="font-display font-bold text-lg text-[var(--brand)]"
                data-testid="brand-title"
              >
                {appName}
              </div>
              <div className="text-xs text-[var(--text-muted)]">
                {t("shell.welcome", {
                  name: user?.user?.full_name?.split(" ")[0] || "",
                })}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <LanguageSelector compact />
              <Button
                variant="ghost"
                size="sm"
                onClick={logout}
                data-testid="logout-btn"
                className="text-[var(--text-secondary)]"
              >
                <LogOut className="h-4 w-4 me-1.5" /> {t("shell.logout")}
              </Button>
            </div>
          </div>
        </header>
        <main className="max-w-2xl mx-auto px-4 py-6">{children}</main>
        <nav className="fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-xl border-t border-[var(--subtle)]">
          <div className="max-w-2xl mx-auto flex justify-around">
            {nav.map((item) => {
              const active = location.pathname === item.to;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={`mnav-${item.label.toLowerCase()}`}
                  className={cn(
                    "flex flex-1 min-w-0 flex-col items-center py-3 px-1 text-xs font-medium transition-colors",
                    active
                      ? "text-[var(--accent)]"
                      : "text-[var(--text-secondary)]",
                  )}
                >
                  <item.icon
                    className="h-5 w-5 mb-1"
                    strokeWidth={active ? 2.5 : 1.75}
                  />
                  {item.label}
                </NavLink>
              );
            })}
          </div>
        </nav>
      </div>
    );
  }

  // Staff desktop sidebar
  return (
    <div className="min-h-screen bg-[var(--bg)] flex flex-col lg:flex-row">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 start-0 w-64 bg-[var(--brand)] flex-col text-white z-40">
        <BrandBadge appName={appName} clientName={clientName} />
        <div className="flex-1 overflow-y-auto">
          <SidebarLinks user={user} t={t} />
        </div>
        <UserFooter user={user} onLogout={logout} />
      </aside>

      {/* Mobile top bar with sheet menu */}
      <header className="lg:hidden sticky top-0 z-30 w-full bg-[var(--brand)] text-white flex items-center justify-between px-4 py-3">
        <Sheet>
          <SheetTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/10"
              data-testid="mobile-menu-btn"
            >
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent
            side={direction === "rtl" ? "right" : "left"}
            className="bg-[var(--brand)] border-e border-white/10 text-white w-64 p-0"
          >
            <SheetTitle className="sr-only">{t("shell.navigation")}</SheetTitle>
            <BrandBadge appName={appName} clientName={clientName} />
            <SidebarLinks user={user} t={t} />
            <UserFooter user={user} onLogout={logout} />
          </SheetContent>
        </Sheet>
        <div className="font-display font-bold text-base">{appName}</div>
        <div className="flex items-center gap-2">
          <LanguageSelector compact />
          <Button
            variant="ghost"
            size="icon"
            onClick={logout}
            className="text-white hover:bg-white/10"
            data-testid="logout-mobile-btn"
          >
            <LogOut className="h-5 w-5" aria-label={t("shell.logout")} />
            <span className="sr-only">{t("shell.logout")}</span>
          </Button>
        </div>
      </header>

      <main className="w-full lg:w-0 min-w-0 flex-1 lg:ms-64 min-h-screen overflow-x-auto">
        {children}
      </main>
    </div>
  );
}
